import {
  Icon,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuLinkItem,
  MenuSeparator,
  MenuMeta,
  useKeyboardHints,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  Text,
} from "@salestrak/ui";
import React from "react";
import { APP_VERSION as APP_VERSION_RAW } from "#app/lib/app-version";
import { CHANGELOG_URL } from "#app/lib/marketing-urls";
import { openKeyboardShortcutsDialog } from "#app/lib/keyboard-shortcuts-dialog-context";
import {
  formatDeviceLabel,
  resolveDeviceContext,
} from "#app/lib/analytics/device-context";

const APP_VERSION = `v${APP_VERSION_RAW}`;

function useClientHintsVersion(): string | null {
  const [version, setVersion] = React.useState<string | null>(null);
  React.useEffect(() => {
    // @ts-expect-error -- userAgentData is not yet in all TS DOM libs
    navigator.userAgentData
      ?.getHighEntropyValues(["platformVersion"])
      .then((hints: { platformVersion?: string }) => {
        if (hints.platformVersion) setVersion(hints.platformVersion);
      })
      .catch(() => {
      });
  }, []);
  return version;
}

/**
 * Help sub-menu for the user avatar menu. Renders ONLY the menu — any dialog it
 * opens (the feedback dialog via `onSendFeedback`, the About dialog via
 * `onAbout`) must be mounted by a parent that stays mounted when the menu
 * closes (`AccountMenu`). Pressing a `MenuItem` closes the dropdown and
 * unmounts everything under the popup, so a dialog rendered here would unmount
 * before it could ever show.
 */
export function HelpSubMenu({
  onAbout,
  onSendFeedback,
}: {
  onAbout: () => void;
  onSendFeedback: () => void;
}) {
  const clientHintsVersion = useClientHintsVersion();
  const keyboardHints = useKeyboardHints();
  const deviceInfo = React.useMemo(
    () => formatDeviceLabel(resolveDeviceContext(clientHintsVersion)),
    [clientHintsVersion],
  );

  return (
    <MenuSub>
      <MenuSubTrigger>
        <Icon color="muted" name="help-circle" size="md" />
        <Text>Help</Text>
      </MenuSubTrigger>
      <MenuSubPopup>
        <MenuGroup>
          {keyboardHints && (
            <MenuItem onClick={openKeyboardShortcutsDialog}>
              <Icon color="muted" name="keyboard" size="md" />
              <Text>Shortcuts</Text>
            </MenuItem>
          )}
          <MenuItem disabled>
            <Icon color="muted" name="book-open" size="md" />
            <Text>Docs</Text>
            <Icon
              className="ms-auto"
              color="muted"
              name="external-link"
              size="sm"
            />
          </MenuItem>
          <MenuItem disabled>
            <Icon color="muted" name="file-code" size="md" />
            <Text>API Docs</Text>
            <Icon
              className="ms-auto"
              color="muted"
              name="external-link"
              size="sm"
            />
          </MenuItem>
          <MenuItem onClick={onSendFeedback}>
            <Icon color="muted" name="message" size="md" />
            <Text>Send feedback</Text>
          </MenuItem>
          <MenuItem disabled>
            <Icon color="muted" name="pen-line" size="md" />
            <Text>Contact us</Text>
          </MenuItem>
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup>
          <MenuGroupLabel>What's new</MenuGroupLabel>
          {/* A real anchor — the link opens through the element. */}
          <MenuLinkItem
            href={CHANGELOG_URL}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Icon color="muted" name="scroll" size="sm" />
            <Text>Full changelog</Text>
            <Icon
              className="ms-auto"
              color="muted"
              name="external-link"
              size="sm"
            />
          </MenuLinkItem>
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup>
          <MenuItem onClick={onAbout}>
            <Icon color="muted" name="info" size="md" />
            <Text>About SalesTrak</Text>
            <MenuMeta>{APP_VERSION}</MenuMeta>
          </MenuItem>
          <MenuItem>
            <Icon color="muted" name="monitor" size="md" />
            <Text truncate>Device info</Text>
            <MenuMeta>{deviceInfo}</MenuMeta>
          </MenuItem>
        </MenuGroup>
      </MenuSubPopup>
    </MenuSub>
  );
}
